"use client"

import { useState, useEffect } from "react"
import { StyleSheet, View, KeyboardAvoidingView, Platform } from "react-native"
import { Text, TextInput, Button, Surface, Snackbar, ActivityIndicator } from "react-native-paper"
import { useTheme } from "../contexts/ThemeContext"
import { useNavigation } from "@react-navigation/native"
import { doc, setDoc, getDoc } from "firebase/firestore"
import { db } from "../config/firebase"
import { useAuth } from "../contexts/AuthContext"
import { Header } from "../components/Header"
import { GradientBackground } from "../components/GradientBackground"

export function ApiConfigScreen() {
  const [apiEndpoint, setApiEndpoint] = useState("")
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [initialLoading, setInitialLoading] = useState(true)
  const { theme } = useTheme()
  const navigation = useNavigation()
  const { user, checkApiEndpoint, apiEndpoint: existingApiEndpoint } = useAuth()

  // Check if the user already has an API endpoint configured
  useEffect(() => {
    const checkExistingEndpoint = async () => {
      if (!user) return

      try {
        // Get the user's settings document from Firestore
        const userSettingsDoc = await getDoc(doc(db, "userSettings", user.uid))

        if (userSettingsDoc.exists() && userSettingsDoc.data().apiEndpoint) {
          // If the API endpoint exists, validate it
          const endpoint = userSettingsDoc.data().apiEndpoint
          setApiEndpoint(endpoint)

          // Validate the existing endpoint
          const isValid = await validateEndpoint(endpoint)

          if (isValid) {
            // If valid, update the auth context and navigate to dashboard
            await checkApiEndpoint()
            navigation.navigate("AllProjectsDashboard" as never)
            return
          }

          // If not valid, let the user enter a new endpoint
          setError("Existing API endpoint is no longer valid. Please enter a new one.")
        }
      } catch (err: any) {
        // Error checking existing API endpoint
      } finally {
        setInitialLoading(false)
      }
    }

    checkExistingEndpoint()
  }, [user, navigation])

  const validateEndpoint = async (endpoint: string) => {
    // Ensure the endpoint ends with a slash
    const formattedEndpoint = endpoint.endsWith("/") ? endpoint : `${endpoint}/`

    try {
      setValidating(true)
      setError("")

      // Try to fetch the assets list from the API
      const response = await fetch(`${formattedEndpoint}assets`)

      if (!response.ok) {
        throw new Error(`API validation failed: ${response.statusText}`)
      }

      const data = await response.json()

      // Check if the response has the expected structure
      if (!data.assets || !Array.isArray(data.assets)) {
        throw new Error("API endpoint returned invalid data format")
      }

      return formattedEndpoint
    } catch (err: any) {
      setError(`Validation failed: ${err.message}`)
      return null
    } finally {
      setValidating(false)
    }
  }

  const saveEndpoint = async (endpoint: string) => {
    if (!user) return

    try {
      // Saving API endpoint for user
      // Save the API endpoint to the user's document in Firestore
      await setDoc(
        doc(db, "userSettings", user.uid),
        {
          apiEndpoint: endpoint,
          updatedAt: new Date(),
        },
        { merge: true },
      )
      // API endpoint saved successfully
      return true
    } catch (err: any) {
      // Failed to save API endpoint
      setError(`Failed to save API endpoint: ${err.message}`)
      return false
    }
  }

  const handleSubmit = async () => {
    if (!apiEndpoint) {
      setError("Please enter an API endpoint")
      return
    }

    try {
      setLoading(true)
      // Validating and saving endpoint

      // Validate the endpoint
      const validatedEndpoint = await validateEndpoint(apiEndpoint)

      if (!validatedEndpoint) {
        // Endpoint validation failed
        return // Error is already set in validateEndpoint
      }

      // Endpoint validated successfully

      // Save the endpoint to Firebase
      const saved = await saveEndpoint(validatedEndpoint)

      if (saved) {
        setSuccess("API endpoint configured successfully!")
        // Endpoint saved, updating auth context

        // Update the auth context with the new API endpoint
        await checkApiEndpoint()

        // Navigate to the dashboard after a short delay
        setTimeout(() => {
          navigation.navigate("AllProjectsDashboard" as never)
        }, 1500)
      }
    } catch (err: any) {
      // Error in handleSubmit
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <GradientBackground>
          <Header showLogout={true} />
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={{ marginTop: 16, color: theme.colors.onSurface }}>Checking API configuration...</Text>
        </GradientBackground>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <GradientBackground>
        <Header showLogout={true} />
        <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : "height"} style={styles.content}>
          <Surface style={[styles.configContainer, { backgroundColor: theme.colors.surface }]}>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>API Configuration</Text>

            <Text style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
              Please enter the base API endpoint for accessing asset data. Example:
              https://ynikhil.pythonanywhere.com/api/
            </Text>

            <TextInput
              label="API Endpoint"
              value={apiEndpoint}
              onChangeText={(text) => {
                setApiEndpoint(text)
                setError("")
                setSuccess("")
              }}
              mode="outlined"
              style={styles.input}
              placeholder="https://example.com/api/"
              autoCapitalize="none"
              disabled={loading || validating}
              theme={theme}
            />

            {validating && (
              <View style={styles.validatingContainer}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text style={{ marginLeft: 8, color: theme.colors.onSurfaceVariant }}>Validating endpoint...</Text>
              </View>
            )}

            <Button
              mode="contained"
              onPress={handleSubmit}
              loading={loading}
              disabled={loading || validating || !apiEndpoint}
              style={styles.submitButton}
            >
              Configure API
            </Button>
          </Surface>
        </KeyboardAvoidingView>
      </GradientBackground>

      <Snackbar
        visible={!!error}
        onDismiss={() => setError("")}
        action={{
          label: "Close",
          onPress: () => setError(""),
        }}
        theme={theme}
        style={{ backgroundColor: theme.colors.errorContainer }}
      >
        <Text style={{ color: theme.colors.error }}>{error}</Text>
      </Snackbar>

      <Snackbar
        visible={!!success}
        onDismiss={() => setSuccess("")}
        theme={theme}
        style={{ backgroundColor: theme.colors.primaryContainer }}
      >
        <Text style={{ color: theme.colors.primary }}>{success}</Text>
      </Snackbar>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  configContainer: {
    padding: 24,
    borderRadius: 12,
    width: "100%",
    maxWidth: 500,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
    textAlign: "center",
  },
  description: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: "center",
    lineHeight: 22,
  },
  input: {
    marginBottom: 16,
  },
  validatingContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  submitButton: {
    paddingVertical: 6,
    marginTop: 8,
  },
})

